import { useEffect, useRef } from 'react';

/**
 * Keeps dashboard data fresh across portals/tabs:
 * - refetch when window regains focus
 * - refetch when browser tab becomes visible
 * - optional interval while tab is visible
 */
export function useAutoRefresh(refreshFn, options = {}) {
  const {
    enabled = true,
    intervalMs = 45000
  } = options;

  const fnRef = useRef(refreshFn);
  fnRef.current = refreshFn;

  useEffect(() => {
    if (!enabled || typeof refreshFn !== 'function') {
      return undefined;
    }

    const run = (silent = true) => {
      try {
        const result = fnRef.current?.(silent);
        if (result && typeof result.catch === 'function') {
          result.catch((err) => console.warn('auto-refresh failed:', err));
        }
      } catch (err) {
        console.warn('auto-refresh failed:', err);
      }
    };

    const onFocus = () => run(true);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        run(true);
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    const timer = intervalMs > 0
      ? setInterval(() => {
        if (document.visibilityState === 'visible') {
          run(true);
        }
      }, intervalMs)
      : null;

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer) clearInterval(timer);
    };
  }, [enabled, intervalMs, refreshFn]);
}
